import {ListItem} from '@github-ui/list-view'
import {graphql, useFragment} from 'react-relay'

import {TEST_IDS} from '../constants/test-ids'
import {getIssueStateIcon, getPullRequestStateIcon} from '../constants/stateIcon'
import type {IssuePullRequestStateIcon$key} from './__generated__/IssuePullRequestStateIcon.graphql'

const stateIconFragment = graphql`
  fragment IssuePullRequestStateIcon on IssueOrPullRequest {
    __typename
    ... on Issue {
      state
      stateReason
      isReadByViewer
    }

    ... on PullRequest {
      isDraft
      isInMergeQueue
      pullRequestState: state
      isReadByViewer
    }
  }
`

type IssuePullRequestStateIconProps = {
  dataKey: IssuePullRequestStateIcon$key
}

export function IssuePullRequestStateIcon({dataKey}: IssuePullRequestStateIconProps) {
  const data = useFragment(stateIconFragment, dataKey)
  const defaultState = getIssueStateIcon(null)
  let {icon, color, description} = defaultState
  let hasNewActivity

  if (data.__typename === 'PullRequest') {
    const state = getIssueEntityState(data.isDraft, data.isInMergeQueue, data.pullRequestState)
    const stateData = getPullRequestStateIcon(state)
    icon = stateData.icon
    color = stateData.color
    description = stateData.description
    hasNewActivity = data.isReadByViewer === false
  }

  if (data.__typename === 'Issue') {
    const stateOrReason = data.state === 'CLOSED' && data.stateReason === 'NOT_PLANNED' ? 'NOT_PLANNED' : data.state

    const state = getIssueStateIcon(stateOrReason)
    icon = state.icon
    color = state.color
    description = state.description
    hasNewActivity = data.isReadByViewer === false
  }

  return (
    <ListItem.LeadingVisual
      newActivity={hasNewActivity}
      icon={icon}
      color={color}
      description={description}
      data-testid={TEST_IDS.listRowStateIcon}
    />
  )
}

function getIssueEntityState(isDraft: boolean, isInMergeQueue: boolean, state: string): string {
  if (isDraft) {
    return 'DRAFT'
  }

  if (isInMergeQueue) {
    return 'IN_MERGE_QUEUE'
  }

  return state
}

try{ IssuePullRequestStateIcon.displayName ||= 'IssuePullRequestStateIcon' } catch {}